import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const ANCHOR_GAP = 18;
const ANCHOR_INDENT = 28;
const VIEWPORT_MARGIN = 12;

export interface AnchorRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

interface Props {
  anchor: AnchorRect;
  onClose: () => void;
  width?: number;
  // Keep the whole card on screen at its natural height: below or above the
  // anchor when it fits there, otherwise overlapping the anchor. For content
  // that must never scroll.
  fitViewport?: boolean;
  children: React.ReactNode;
}

export default function Popover({ anchor, onClose, width = 360, fitViewport = false, children }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; maxHeight: number }>({
    left: anchor.left + ANCHOR_INDENT,
    top: anchor.bottom + ANCHOR_GAP,
    // Measuring the natural height needs the card unconstrained on first layout.
    maxHeight: fitViewport
      ? window.innerHeight - 2 * VIEWPORT_MARGIN
      : window.innerHeight - anchor.bottom - ANCHOR_GAP - VIEWPORT_MARGIN
  });

  useLayoutEffect(() => {
    const card = ref.current;
    if (!card) return;
    const cardH = card.offsetHeight;
    const cardW = card.offsetWidth;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Vertical: below if it fits, else above; if neither fits well, pin to the
    // side with more room and let the card scroll internally.
    const spaceBelow = vh - anchor.bottom - ANCHOR_GAP - VIEWPORT_MARGIN;
    const spaceAbove = anchor.top - ANCHOR_GAP - VIEWPORT_MARGIN;
    let top: number;
    let maxHeight: number;
    if (fitViewport && cardH > spaceBelow && cardH > spaceAbove) {
      maxHeight = vh - 2 * VIEWPORT_MARGIN;
      top = Math.max(VIEWPORT_MARGIN, Math.min(anchor.top, vh - VIEWPORT_MARGIN - cardH));
    } else if (fitViewport && cardH > spaceBelow) {
      maxHeight = spaceAbove;
      top = anchor.top - ANCHOR_GAP - cardH;
    } else if (cardH <= spaceBelow || spaceBelow >= spaceAbove) {
      top = anchor.bottom + ANCHOR_GAP;
      maxHeight = spaceBelow;
    } else {
      maxHeight = spaceAbove;
      top = Math.max(VIEWPORT_MARGIN, anchor.top - ANCHOR_GAP - Math.min(cardH, maxHeight));
    }

    // Horizontal: when the anchor is in the left half of the viewport, indent
    // the popover to the right of the anchor's left edge. When it's in the
    // right half, indent it to the left of the anchor's right edge. Either
    // way the borders never line up flush with the anchor.
    const anchorMid = (anchor.left + anchor.right) / 2;
    const isLeftHalf = anchorMid < vw / 2;
    let left = isLeftHalf
      ? anchor.left + ANCHOR_INDENT
      : anchor.right - cardW - ANCHOR_INDENT;
    const maxLeft = vw - cardW - VIEWPORT_MARGIN;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));

    setPos({ left, top, maxHeight });
  }, [anchor, fitViewport]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    function onClick(e: MouseEvent) {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      onClose();
    }
    document.addEventListener('keydown', onKey);
    // Defer to next tick so the click that opened the popover doesn't close it.
    const t = setTimeout(() => document.addEventListener('mousedown', onClick), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
      clearTimeout(t);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      className="fixed z-40 flex flex-col overflow-hidden rounded-lg border border-[var(--border-strong)] bg-[var(--popover-bg)] shadow-2xl"
      style={{
        left: pos.left,
        top: pos.top,
        width,
        maxHeight: pos.maxHeight
      }}
    >
      {children}
    </div>
  );
}
